import { useEffect, useState, type ReactNode } from "react"
import {
  CalendarIcon,
  Mail,
  Phone,
  User2,
  Users,
  PlusIcon,
  MinusIcon,
} from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { BOOKING_REQUEST_WEBHOOK_URL } from "@/lib/config"
import { useBooking } from "@/context/BookingContext"
import { getUserMessage } from "@/lib/api/errors"
import { AirportCombobox } from "@/components/AirportCombobox"
import type { BookingRequestFormValues, PackageType } from "@/types/booking"
import { formatDateLabel } from "@/lib/booking"

type FieldKey = "firstName" | "lastName" | "email" | "phone" | "travelFrom" | "travelTo" | "departureAirport" | "destinationAirport"

function validateRequestFields(
  values: BookingRequestFormValues,
  hasFlights: boolean,
  departureAirportsCount: number,
  destinationAirport: unknown
): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {}
  if (values.firstName.trim() === "") errors.firstName = "First name is required."
  if (values.lastName.trim() === "") errors.lastName = "Last name is required."
  if (values.email.trim() === "") errors.email = "Email is required."
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = "Please enter a valid email address."
  if (values.phone.trim() === "") errors.phone = "Phone number is required."
  else if (!/^\+\d/.test(values.phone.trim())) errors.phone = "Phone number must include a country code (e.g., +421)."
  if (values.travelFrom === "") errors.travelFrom = "Departure date is required."
  if (values.travelTo === "") errors.travelTo = "Return date is required."
  if (hasFlights && departureAirportsCount === 0) errors.departureAirport = "Departure airport is required."
  if (hasFlights && !destinationAirport) errors.destinationAirport = "Destination airport is required."
  return errors
}

interface CustomDatesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialValues?: Partial<BookingRequestFormValues>
  packageType?: PackageType
}

const DEFAULT_VALUES: BookingRequestFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  departureAirport: "",
  destinationAirport: "",
  travelFrom: "",
  travelTo: "",
  travelers: 1,
}

function parseDate(value: string): Date | undefined {
  if (!value) return undefined
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d
}

function formatDateISO(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export function CustomDatesModal({
  open,
  onOpenChange,
  initialValues,
  packageType,
}: CustomDatesModalProps) {
  const { state } = useBooking()
  const [formValues, setFormValues] =
    useState<BookingRequestFormValues>(DEFAULT_VALUES)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fromOpen, setFromOpen] = useState(false)
  const [toOpen, setToOpen] = useState(false)
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)

  useEffect(() => {
    if (!open) return

    setFormValues({
      ...DEFAULT_VALUES,
      ...initialValues,
      travelers: initialValues?.travelers ?? DEFAULT_VALUES.travelers,
    })
  }, [initialValues, open])

  const fromDate = parseDate(formValues.travelFrom)
  const toDate = parseDate(formValues.travelTo)

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)

  const hasFlights = packageType !== "hotel-only"

  const fieldErrors = validateRequestFields(formValues, hasFlights, state.departureAirports.length, state.destinationAirport)
  const hasFieldErrors = Object.keys(fieldErrors).length > 0

  function showError(key: FieldKey): string | undefined {
    return touched[key] || submitAttempted ? fieldErrors[key] : undefined
  }

  function updateField<Key extends keyof BookingRequestFormValues>(
    key: Key,
    value: BookingRequestFormValues[Key]
  ) {
    setFormValues((current) => ({ ...current, [key]: value }))
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()

    setSubmitAttempted(true)
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      travelFrom: true,
      travelTo: true,
      departureAirport: true,
      destinationAirport: true,
    })

    if (hasFieldErrors) return

    setIsSubmitting(true)

    try {
      const response = await fetch(BOOKING_REQUEST_WEBHOOK_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formValues,
          packageType: packageType ?? state.packageType,
          eventName: state.event?.name ?? null,
          eventId: state.event?.id ?? null,
          departureAirport: state.departureAirports.map((a) => a.code).join(", ") || formValues.departureAirport,
          destinationAirport: state.destinationAirport?.code ?? formValues.destinationAirport,
          submittedAt: new Date().toISOString(),
          source: "booking-request-modal",
        }),
      })

      if (!response.ok) {
        throw new Error("Booking request could not be submitted.")
      }

      toast.success("Booking request sent. We will contact you shortly.")
      onOpenChange(false)
    } catch (error) {
      toast.error(getUserMessage(error, "booking-request"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-h-[min(90dvh,720px)] !w-[min(calc(100vw-1rem),42rem)] !max-w-[min(calc(100vw-1rem),42rem)] !overflow-hidden border-border bg-card !p-0">
        <DialogHeader className="border-b border-border px-5 pt-5 pb-4 sm:px-6">
          <DialogTitle className="text-xl font-bold tracking-wide uppercase">
            Booking Request
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
          <div className="flex max-h-[calc(min(90dvh,720px)-11rem)] flex-col gap-5 overflow-y-auto px-5 py-5 sm:px-6 scrollbar-thin">
          {hasFlights && (
            <>
              <Field label="Flight from" icon={<CalendarIcon className="size-4" />} required error={showError("departureAirport")}>
                <AirportCombobox />
              </Field>

              <Field label="Flight to" icon={<CalendarIcon className="size-4" />} required error={showError("destinationAirport")}>
                <AirportCombobox mode="destination" />
              </Field>
            </>
          )}

          <Field label="Travelers" icon={<Users className="size-4" />}>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() =>
                  updateField(
                    "travelers",
                    Math.max(1, formValues.travelers - 1)
                  )
                }
              >
                <MinusIcon />
              </Button>
              <div className="flex size-8 items-center justify-center rounded-lg border border-primary/40 bg-primary/10 px-4 text-base font-semibold text-primary">
                {formValues.travelers}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() =>
                  updateField(
                    "travelers",
                    Math.min(20, formValues.travelers + 1)
                  )
                }
              >
                <PlusIcon />
              </Button>
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Travel From"
              icon={<CalendarIcon className="size-4" />}
              required
              error={showError("travelFrom")}
            >
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger
                  className={`inline-flex min-h-11 w-full items-center justify-start rounded-lg border bg-input/30 px-4 text-left text-sm font-normal text-foreground hover:bg-input/60 ${fieldErrors.travelFrom && (touched.travelFrom || submitAttempted) ? "border-destructive" : "border-input"}`}
                  type="button"
                >
                  <CalendarIcon className="mr-2 size-4 text-muted-foreground" />
                  {fromDate
                    ? formatDateLabel(formatDateISO(fromDate))
                    : "Pick a date"}
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto rounded-xl border-border bg-card p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={fromDate}
                    defaultMonth={fromDate ?? tomorrow}
                    onSelect={(date) => {
                      if (date) {
                        updateField("travelFrom", formatDateISO(date))
                        setFromOpen(false)
                      }
                    }}
                    disabled={(date) => date < tomorrow}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="Travel To" icon={<CalendarIcon className="size-4" />} required error={showError("travelTo")}>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger
                  className={`inline-flex min-h-11 w-full items-center justify-start rounded-lg border bg-input/30 px-4 text-left text-sm font-normal text-foreground hover:bg-input/60 ${fieldErrors.travelTo && (touched.travelTo || submitAttempted) ? "border-destructive" : "border-input"}`}
                  type="button"
                >
                  <CalendarIcon className="mr-2 size-4 text-muted-foreground" />
                  {toDate
                    ? formatDateLabel(formatDateISO(toDate))
                    : "Pick a date"}
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto rounded-xl border-border bg-card p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={toDate}
                    defaultMonth={toDate ?? fromDate}
                    onSelect={(date) => {
                      if (date) {
                        updateField("travelTo", formatDateISO(date))
                        setToOpen(false)
                      }
                    }}
                    disabled={(date) => date < (fromDate ?? tomorrow)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First Name" icon={<User2 className="size-4" />} required error={showError("firstName")}>
              <Input
                value={formValues.firstName}
                onChange={(event) =>
                  updateField("firstName", event.target.value)
                }
                onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
                aria-invalid={(touched.firstName || submitAttempted) && fieldErrors.firstName ? true : undefined}
                placeholder="John"
                required
              />
            </Field>

            <Field label="Last Name" icon={<User2 className="size-4" />} required error={showError("lastName")}>
              <Input
                value={formValues.lastName}
                onChange={(event) =>
                  updateField("lastName", event.target.value)
                }
                onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
                aria-invalid={(touched.lastName || submitAttempted) && fieldErrors.lastName ? true : undefined}
                placeholder="Doe"
                required
              />
            </Field>

            <Field label="Email" icon={<Mail className="size-4" />} required error={showError("email")}>
              <Input
                type="email"
                value={formValues.email}
                onChange={(event) => updateField("email", event.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                aria-invalid={(touched.email || submitAttempted) && fieldErrors.email ? true : undefined}
                placeholder="john@example.com"
                required
              />
            </Field>

            <Field label="Phone" icon={<Phone className="size-4" />} required error={showError("phone")}>
              <Input
                type="tel"
                value={formValues.phone}
                onChange={(event) => updateField("phone", event.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                aria-invalid={(touched.phone || submitAttempted) && fieldErrors.phone ? true : undefined}
                placeholder="+421 900 000 000"
                required
              />
            </Field>
          </div>

          </div>

          <div className="flex gap-3 border-t border-border bg-muted/30 px-5 py-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              size="xl"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              size="xl"
              className="flex-1"
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Field({
  label,
  icon,
  required,
  error,
  children,
}: {
  label: string
  icon: ReactNode
  required?: boolean
  error?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="text-muted-foreground">{icon}</span>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </div>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  )
}
